import { isApiError } from '@pa/api'
import {
  getGetClinicQueryKey,
  getGetConnectionQueryKey,
  getListClinicsQueryKey,
  useGetClinic,
  useGetConnection,
  usePutConnection,
  useTestConnection,
  type ConnectionTestResult,
} from '@pa/api/admin'
import { toastApiError } from '@pa/staff'
import { useToast } from '@pa/ui'
import { useQueryClient } from '@tanstack/react-query'
import { PlugZap } from 'lucide-react'
import { useId, useState, type SyntheticEvent } from 'react'
import { API_KEY_RE, ConnectionResult, messageFor } from '@/features/clinics'
import { SecurityCodesCard } from './SecurityCodesCard'

interface Errors {
  baseUrl?: string
  apiKey?: string
}

/**
 * S-04 Connection (UserFlows S-04; API.md §7.8–§7.10): the PF Keeper address, the API key masked (replaceable,
 * never shown), and Test connection with its result fields.
 */
export function ConnectionTab({ clinicId }: { clinicId: string }) {
  const id = useId()
  const toast = useToast()
  const queryClient = useQueryClient()
  const query = useGetConnection(clinicId, { query: { staleTime: 30_000 } })
  // Task 25: the automatic PF security codes card reads the clinic (#40; the page already loaded it).
  const clinic = useGetClinic(clinicId, { query: { staleTime: 30_000 } })
  const put = usePutConnection()
  const test = useTestConnection()
  const [baseUrl, setBaseUrl] = useState<string | null>(null)
  const [apiKey, setApiKey] = useState('')
  const [errors, setErrors] = useState<Errors>({})
  const [result, setResult] = useState<ConnectionTestResult | null>(null)
  const saved = query.data
  // The address field starts from the saved address until the user types (no effect needed).
  const address = baseUrl ?? saved?.baseUrl ?? ''

  const refreshClinic = () => {
    void queryClient.invalidateQueries({ queryKey: getGetClinicQueryKey(clinicId) })
    void queryClient.invalidateQueries({ queryKey: getListClinicsQueryKey() })
  }

  async function save(e: SyntheticEvent) {
    e.preventDefault()
    const next: Errors = {}
    if (address.trim() === '') next.baseUrl = messageFor('base_url_invalid')
    if (apiKey !== '' && !API_KEY_RE.test(apiKey)) next.apiKey = messageFor('api_key_invalid')
    if (apiKey === '' && !saved?.apiKeySet) next.apiKey = messageFor('api_key_required')
    setErrors(next)
    if (next.baseUrl ?? next.apiKey) return
    try {
      const info = await put.mutateAsync({
        clinicId,
        data: { baseUrl: address.trim(), ...(apiKey !== '' && { apiKey }) },
      })
      queryClient.setQueryData(getGetConnectionQueryKey(clinicId), info)
      setApiKey('')
      refreshClinic()
      toast('Connection saved')
    } catch (err) {
      if (isApiError(err) && err.code === 'invalid_request' && err.issues?.length) {
        const mapped: Errors = {}
        for (const issue of err.issues) {
          if (issue.path[0] === 'baseUrl') mapped.baseUrl = messageFor(issue.code)
          if (issue.path[0] === 'apiKey') mapped.apiKey = messageFor(issue.code)
        }
        if (mapped.baseUrl ?? mapped.apiKey) {
          setErrors(mapped)
          return
        }
      }
      toastApiError(toast, err)
    }
  }

  async function runTest() {
    try {
      const r = await test.mutateAsync({ clinicId })
      setResult(r)
      void queryClient.invalidateQueries({ queryKey: getGetConnectionQueryKey(clinicId) })
      refreshClinic()
    } catch (err) {
      toastApiError(toast, err)
    }
  }

  if (query.isPending) {
    return (
      <div className="card card-pad" role="status">
        Loading the connection…
      </div>
    )
  }
  if (query.isError) {
    return (
      <div className="card card-pad" role="alert">
        Couldn&apos;t load the connection.{' '}
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => void query.refetch()}>
          Retry
        </button>
      </div>
    )
  }

  const shown = result ?? query.data.lastTest
  return (
    <div className="grid two">
      <section className="card card-pad form-grid" aria-labelledby={`${id}-title`}>
        <h2 className="form-section-title" id={`${id}-title`}>
          PF Keeper
        </h2>
        <dl className="kv">
          <dt>Address</dt>
          <dd className="mono">{query.data.baseUrl ?? 'Not set'}</dd>
          <dt>API key</dt>
          <dd className="mono">{query.data.apiKeyLast4 ? `••••${query.data.apiKeyLast4}` : 'Not set'}</dd>
        </dl>
        <form className="form-grid" onSubmit={(e) => void save(e)} noValidate>
          <div className="field">
            <label className="label" htmlFor={`${id}-baseUrl`}>
              PF Keeper address
            </label>
            <input
              id={`${id}-baseUrl`}
              className="input mono"
              placeholder="https://10.0.0.5:8443"
              autoComplete="off"
              value={address}
              aria-invalid={errors.baseUrl ? true : undefined}
              aria-describedby={errors.baseUrl ? `${id}-baseUrl-error` : undefined}
              onChange={(e) => {
                setBaseUrl(e.target.value)
              }}
            />
            {errors.baseUrl && (
              <span className="err-text" id={`${id}-baseUrl-error`}>
                {errors.baseUrl}
              </span>
            )}
          </div>
          <div className="field">
            <label className="label" htmlFor={`${id}-apiKey`}>
              {query.data.apiKeySet ? 'Replace API key' : 'API key'}
            </label>
            <input
              id={`${id}-apiKey`}
              className="input mono"
              type="password"
              autoComplete="off"
              value={apiKey}
              aria-invalid={errors.apiKey ? true : undefined}
              aria-describedby={errors.apiKey ? `${id}-apiKey-error` : `${id}-apiKey-hint`}
              onChange={(e) => {
                setApiKey(e.target.value)
              }}
            />
            {errors.apiKey ? (
              <span className="err-text" id={`${id}-apiKey-error`}>
                {errors.apiKey}
              </span>
            ) : (
              <span className="hint" id={`${id}-apiKey-hint`}>
                {query.data.apiKeySet ? 'Leave empty to keep the current key.' : 'From PF Keeper’s tray: Copy API key.'}
              </span>
            )}
          </div>
          <div className="row">
            <button type="submit" className="btn btn-primary" disabled={put.isPending}>
              {put.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </section>
      <section className="card card-pad form-grid" aria-labelledby={`${id}-test-title`}>
        <h2 className="form-section-title" id={`${id}-test-title`}>
          Test connection
        </h2>
        <p className="muted">Checks that PF Keeper answers, accepts the key and is logged in to Practice Fusion.</p>
        <div className="row">
          <button
            type="button"
            className="btn btn-secondary"
            disabled={!query.data.apiKeySet || test.isPending}
            onClick={() => void runTest()}
          >
            <PlugZap aria-hidden /> {test.isPending ? 'Testing…' : 'Test connection'}
          </button>
        </div>
        {shown ? <ConnectionResult result={shown} /> : <p className="muted">Not tested yet.</p>}
      </section>
      {clinic.data && <SecurityCodesCard clinic={clinic.data} />}
    </div>
  )
}
